/**
 * /operations/loans/article-90-watch — Session 3a PR 14.
 *
 * Real-time 1/3-wage-cap proximity monitoring. Mutaba'aa-owned per
 * Session 3a §4.13 (this IS a Mutaba'aa compliance scan surface —
 * the visible side of the veto path).
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Mutaba'aa narration)
 *   2. InlineAISuggestion cards (active veto preview · clustering
 *      pattern)
 *   3. TraceableValue wrapping monthly_wage + current_deductions +
 *      article_90_cap + headroom (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { Scan, ShieldAlert, ShieldX } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type Article90WatchItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "./_session3aHelpers";

type Filter = "all" | "critical" | "attention" | "over_cap";

function statusTone(s: Article90WatchItem["status"]): "neutral" | "attention" | "critical" | "veto" {
  if (s === "over_cap") return "veto";
  if (s === "critical") return "critical";
  if (s === "attention") return "attention";
  return "neutral";
}

export function Article90WatchPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [filter, setFilter] = useState<Filter>("all");

  const list = useQuery({
    queryKey: ["article-90-watch", legalEntityId],
    queryFn: () => session3aApi.listArticle90Watch(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: Article90WatchItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const critical = items.filter((r) => r.status === "critical");
    const overCap = items.filter((r) => r.status === "over_cap");
    const attention = items.filter((r) => r.status === "attention");
    const avg =
      items.length > 0
        ? items.reduce((acc, r) => acc + r.headroom_pct, 0) / items.length
        : 0;
    return {
      total: items.length,
      critical: critical.length,
      overCap: overCap.length,
      attention: attention.length,
      avgHeadroom: avg,
    };
  }, [items]);

  const filtered = useMemo(() => {
    switch (filter) {
      case "critical":
        return items.filter((r) => r.status === "critical");
      case "attention":
        return items.filter((r) => r.status === "attention");
      case "over_cap":
        return items.filter((r) => r.status === "over_cap");
      default:
        return items;
    }
  }, [items, filter]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.article_90_watch.title", "Article 90 Watch")}
          subtitle={t(
            "operations.article_90_watch.subtitle",
            "Real-time 1/3-wage-cap proximity monitoring. Mutaba'aa's veto path made visible.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["all", "All"],
                ["attention", "Attention (10-20%)"],
                ["critical", "Critical (<10%)"],
                ["over_cap", "Over-cap (vetoed)"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  filter === key
                    ? key === "over_cap"
                      ? "border-purple-300 bg-purple-50 text-purple-800"
                      : key === "critical"
                        ? "border-rose-300 bg-rose-50 text-rose-800"
                        : key === "attention"
                          ? "border-amber-300 bg-amber-50 text-amber-800"
                          : "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`operations.article_90_watch.filter.${key}`, label)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "operations.article_90_watch.scan_note",
                  "Manual trigger runs the Article90Scanner from M3X.1 v2 immediately. APScheduler also runs hourly.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Scan className="h-4 w-4" />
            {t(
              "operations.article_90_watch.cta.scan",
              "Run Mutaba'aa audit now",
            )}
          </button>
        </div>

        {stats.overCap > 0 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<ShieldX className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "operations.article_90_watch.suggestion.veto_headline",
              `Mutaba'aa veto active: ${stats.overCap} pending approvals would breach Article 90 — auto-vetoed.`,
            )}
            body={t(
              "operations.article_90_watch.suggestion.veto_body",
              "These employees cannot receive new loans or deductions until headroom improves. Mutaba'aa logs the veto event.",
            )}
            primaryAction={{
              label: t(
                "operations.article_90_watch.suggestion.review_vetoed",
                "Review vetoed",
              ),
              onClick: () => setFilter("over_cap"),
            }}
            suggestionKey={`article-90-veto-${stats.overCap}`}
          />
        )}
        {stats.critical > 3 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<ShieldAlert className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.article_90_watch.suggestion.cluster_headline",
              `Pattern: ${stats.critical} employees clustering near 90% cap utilization.`,
            )}
            body={t(
              "operations.article_90_watch.suggestion.cluster_body",
              "Departmental review suggested. Often signals a comp lag forcing salary advances.",
            )}
            primaryAction={{
              label: t(
                "operations.article_90_watch.suggestion.open_comp",
                "Open Compensation report",
              ),
              onClick: () => window.location.assign("/reports/compensation"),
            }}
            suggestionKey={`article-90-cluster-${stats.critical}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.wage", "Monthly wage")}</th>
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.deductions", "Current deductions")}</th>
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.cap", "Art. 90 cap")}</th>
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.headroom", "Headroom")}</th>
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("operations.article_90_watch.col.last_scan", "Last scan")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const tone = statusTone(row.status);
                  return (
                    <tr
                      key={row.employee_id}
                      className="border-t border-border-subtle hover:bg-surface-sunken/40"
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/people/employees/${row.employee_id}`}
                          className="font-medium text-[#2260C9] hover:underline"
                        >
                          {row.employee_name_en}
                        </Link>
                        <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.article_90_watch.trace.wage",
                                `monthly_wage = ContractORM.basic_salary + sum(active allowances). Source: M12 contract resolver + M14 allowance roll-up.`,
                              ),
                            )
                          }
                        >
                          <span dir="ltr">{formatSar(row.monthly_wage_sar)}</span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.article_90_watch.trace.deductions",
                                `current_deductions = active M16 loan installments + M15 unpaid leaves + garnishments + advances. Live aggregation.`,
                              ),
                            )
                          }
                        >
                          <span dir="ltr">{formatSar(row.current_deductions_sar)}</span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.article_90_watch.trace.cap",
                                `Article 90 cap = monthly_wage / 3 = ${formatSar(row.article_90_cap_sar)}. Statutory floor — Mutaba'aa never relaxes (commandment 45).`,
                              ),
                            )
                          }
                        >
                          <span dir="ltr">{formatSar(row.article_90_cap_sar)}</span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.article_90_watch.trace.headroom",
                                `headroom = cap − current_deductions. Negative headroom = over-cap = veto.`,
                              ),
                            )
                          }
                        >
                          <span
                            className={`text-xs ${
                              tone === "veto"
                                ? "text-purple-700"
                                : tone === "critical"
                                  ? "text-rose-700"
                                  : "text-text-secondary"
                            }`}
                          >
                            <span dir="ltr">{formatSar(row.headroom_sar)}</span>{" "}
                            ({row.headroom_pct.toFixed(0)}%)
                          </span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(tone)}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-text-secondary">
                        <span dir="ltr">
                          {row.last_mutabaa_scan_at?.slice(11, 16) ?? "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.article_90_watch.empty",
              "All employees within Article 90 cap. ✦ Mutaba'aa: no violations detected.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: stats.total,
            attention: stats.attention,
            critical: stats.critical + stats.overCap,
            top_kind: t("operations.article_90_watch.title", "Article 90 Watch"),
            window_label: "live",
          }}
        />
      </aside>
    </div>
  );
}
